/**
 * Typed wrappers around the Phase C1 community endpoints.
 *
 * Backend reference: ``backend/caitapi/routers/community.py``. Types
 * mirror the Pydantic response models 1:1 — keep in sync when those
 * change.
 *
 * Auth: every endpoint requires an authenticated session (401) plus a
 * community-member context (403 with ``blocking="not_member"`` for
 * users without active enrollment, non-revoked cert, or admin role).
 * The SPA's standard 401-handler redirects through
 * ``/api/auth/login?return_to=...``; the 403 path renders a
 * "join the cohort / get certified" CTA in the page shell.
 *
 * Phase C2 (profiles + directory) extends this client with profile
 * + directory + search wrappers in-place.
 */
import { apiDelete, apiGet, apiPatch, apiPost, apiPut } from "./api";

// ---------------------------------------------------------------------------
// Shared types
// ---------------------------------------------------------------------------

export type ThreadStatus = "active" | "hidden" | "deleted";
export type ReactionKind = "like" | "helpful" | "insightful" | "thanks";
export type TargetType = "thread" | "reply";
export type ThreadSort = "activity" | "new" | "top";

export interface CategoryRead {
  id: string;
  slug: string;
  title: string;
  description: string | null;
  sequence: number;
  is_announcement: boolean;
  requires_deid_ack: boolean;
  requires_certification: boolean;
}

export interface AuthorRef {
  user_id: string;
  display_name: string;
  handle: string | null;
  credential_visible: boolean;
  is_certified: boolean;
}

export interface ContextModuleRef {
  module_id: string;
  sequence: number;
  slug: string;
  title: string;
}

export interface ReactionCount {
  reaction: ReactionKind;
  count: number;
  by_me: boolean;
}

export interface ThreadRead {
  id: string;
  category_id: string;
  slug: string;
  title: string;
  body_md: string;
  author: AuthorRef;
  is_pinned: boolean;
  is_locked: boolean;
  status: ThreadStatus;
  reply_count: number;
  last_activity_at: string;
  context_module: ContextModuleRef | null;
  created_at: string;
  updated_at: string;
}

export interface ThreadListItem {
  id: string;
  slug: string;
  title: string;
  author: AuthorRef;
  is_pinned: boolean;
  is_locked: boolean;
  reply_count: number;
  last_activity_at: string;
  created_at: string;
  context_module: ContextModuleRef | null;
}

export interface ReplyRead {
  id: string;
  thread_id: string;
  body_md: string;
  author: AuthorRef;
  parent_reply_id: string | null;
  status: ThreadStatus;
  created_at: string;
  updated_at: string;
  reactions: ReactionCount[];
}

export interface ThreadDetail {
  thread: ThreadRead;
  replies: ReplyRead[];
  reactions: ReactionCount[];
  subscribed: boolean;
  /** True iff the current user is the author or an admin. */
  can_edit: boolean;
  /** Pre-computed gate state mirroring the POST-reply check. */
  can_post_reply: boolean;
}

// ---------------------------------------------------------------------------
// Categories
// ---------------------------------------------------------------------------

export interface CategoryListResponse {
  categories: CategoryRead[];
}

export function fetchCategories(): Promise<CategoryRead[]> {
  return apiGet<CategoryListResponse>("/api/community/categories").then(
    (r) => r.categories,
  );
}

// ---------------------------------------------------------------------------
// Threads
// ---------------------------------------------------------------------------

export interface ThreadListResponse {
  category: CategoryRead;
  threads: ThreadListItem[];
  total: number;
  next_offset: number | null;
}

export interface FetchThreadsOptions {
  sort?: ThreadSort;
  limit?: number;
  offset?: number;
}

export function fetchThreads(
  slug: string,
  opts?: FetchThreadsOptions,
): Promise<ThreadListResponse> {
  const params = new URLSearchParams();
  if (opts?.sort) params.set("sort", opts.sort);
  if (opts?.limit !== undefined) params.set("limit", String(opts.limit));
  if (opts?.offset !== undefined) params.set("offset", String(opts.offset));
  const qs = params.toString() ? `?${params.toString()}` : "";
  return apiGet<ThreadListResponse>(
    `/api/community/categories/${encodeURIComponent(slug)}/threads${qs}`,
  );
}

export interface ThreadCreateBody {
  title: string;
  body_md: string;
  context_module_id?: string;
  /** Required `true` when the category has ``requires_deid_ack``. */
  deid_ack?: boolean;
}

export interface ThreadCreatedResponse {
  thread: ThreadRead;
}

export function createThread(
  slug: string,
  body: ThreadCreateBody,
): Promise<ThreadRead> {
  return apiPost<ThreadCreatedResponse>(
    `/api/community/categories/${encodeURIComponent(slug)}/threads`,
    body,
  ).then((r) => r.thread);
}

export function fetchThread(threadId: string): Promise<ThreadDetail> {
  return apiGet<ThreadDetail>(`/api/community/threads/${threadId}`);
}

export interface ThreadOrReplyEditBody {
  /** Author-only field. */
  body_md?: string;
  /** Author-only, threads only. */
  title?: string;
  /** Admin-only. */
  is_pinned?: boolean;
  /** Admin-only. */
  is_locked?: boolean;
  /** Admin-only. ``active`` un-hides, ``hidden`` soft-hides. */
  status?: "active" | "hidden";
}

export function patchThread(
  threadId: string,
  body: ThreadOrReplyEditBody,
): Promise<ThreadRead> {
  return apiPatch<ThreadRead>(`/api/community/threads/${threadId}`, body);
}

export function deleteThread(threadId: string): Promise<void> {
  return apiDelete<void>(`/api/community/threads/${threadId}`);
}

// ---------------------------------------------------------------------------
// Replies
// ---------------------------------------------------------------------------

export interface ReplyCreateBody {
  body_md: string;
  /** Optional one-level nesting; backend rejects depth > 1. */
  parent_reply_id?: string;
}

export function createReply(
  threadId: string,
  body: ReplyCreateBody,
): Promise<ReplyRead> {
  return apiPost<ReplyRead>(
    `/api/community/threads/${threadId}/replies`,
    body,
  );
}

export function patchReply(
  replyId: string,
  body: ThreadOrReplyEditBody,
): Promise<ReplyRead> {
  return apiPatch<ReplyRead>(`/api/community/replies/${replyId}`, body);
}

export function deleteReply(replyId: string): Promise<void> {
  return apiDelete<void>(`/api/community/replies/${replyId}`);
}

// ---------------------------------------------------------------------------
// Reactions (toggle)
// ---------------------------------------------------------------------------

export interface ReactionToggleResponse {
  reaction: ReactionKind;
  target_type: TargetType;
  target_id: string;
  /** State AFTER the toggle. */
  now_active: boolean;
  /** Total count of this reaction on this target after the toggle. */
  count: number;
}

export function toggleReaction(
  targetType: TargetType,
  targetId: string,
  reaction: ReactionKind,
): Promise<ReactionToggleResponse> {
  return apiPost<ReactionToggleResponse>(
    `/api/community/${targetType}/${targetId}/reactions`,
    { reaction },
  );
}

// ---------------------------------------------------------------------------
// Subscriptions
// ---------------------------------------------------------------------------

export interface SubscribeResponse {
  subscribed: boolean;
}

export function subscribeToThread(threadId: string): Promise<SubscribeResponse> {
  return apiPost<SubscribeResponse>(
    `/api/community/threads/${threadId}/subscribe`,
  );
}

export function unsubscribeFromThread(
  threadId: string,
): Promise<SubscribeResponse> {
  return apiDelete<SubscribeResponse>(
    `/api/community/threads/${threadId}/subscribe`,
  );
}

// ---------------------------------------------------------------------------
// Phase C2 — Profiles + Directory
// ---------------------------------------------------------------------------

export type DmPref = "everyone" | "nobody";

export interface ProfileRead {
  id: string;
  user_id: string;
  handle: string | null;
  headline: string | null;
  bio: string | null;
  specialties: string[];
  region: string | null;
  website_url: string | null;
  linkedin_url: string | null;
  avatar_url: string | null;
  is_listed: boolean;
  credential_visible: boolean;
  dm_pref: DmPref;
  display_name: string;
  is_certified: boolean;
  created_at: string;
  updated_at: string;
}

export interface ProfileEditBody {
  handle?: string;
  headline?: string;
  bio?: string;
  specialties?: string[];
  region?: string;
  website_url?: string;
  linkedin_url?: string;
  is_listed?: boolean;
  credential_visible?: boolean;
  dm_pref?: DmPref;
}

export interface DirectoryEntry {
  user_id: string;
  handle: string | null;
  display_name: string;
  headline: string | null;
  region: string | null;
  specialties: string[];
  avatar_url: string | null;
  credential_visible: boolean;
  is_certified: boolean;
}

export interface DirectoryResponse {
  members: DirectoryEntry[];
  total: number;
  next_offset: number | null;
}

export interface DirectoryFilters {
  /** Free-text search across handle/headline/bio/specialties (Postgres FTS). */
  q?: string;
  /** Exact-match specialty (JSONB array containment). */
  specialty?: string;
  /** Substring match on region (ILIKE). */
  region?: string;
  /** When true, post-filter to members with a non-revoked certificate. */
  certified?: boolean;
  limit?: number;
  offset?: number;
}

export interface MemberThreadSummary {
  id: string;
  slug: string;
  title: string;
  category_slug: string;
  reply_count: number;
  last_activity_at: string;
  created_at: string;
}

export interface MemberDetailResponse {
  profile: ProfileRead;
  recent_threads: MemberThreadSummary[];
}

export function fetchMyProfile(): Promise<ProfileRead> {
  return apiGet<ProfileRead>("/api/community/me/profile");
}

export function updateMyProfile(body: ProfileEditBody): Promise<ProfileRead> {
  return apiPut<ProfileRead>("/api/community/me/profile", body);
}

export function fetchDirectory(
  filters?: DirectoryFilters,
): Promise<DirectoryResponse> {
  const params = new URLSearchParams();
  if (filters?.q) params.set("q", filters.q);
  if (filters?.specialty) params.set("specialty", filters.specialty);
  if (filters?.region) params.set("region", filters.region);
  if (filters?.certified) params.set("certified", "true");
  if (filters?.limit !== undefined) params.set("limit", String(filters.limit));
  if (filters?.offset !== undefined) params.set("offset", String(filters.offset));
  const qs = params.toString() ? `?${params.toString()}` : "";
  return apiGet<DirectoryResponse>(`/api/community/directory${qs}`);
}

export function fetchMember(handleOrId: string): Promise<MemberDetailResponse> {
  return apiGet<MemberDetailResponse>(
    `/api/community/members/${encodeURIComponent(handleOrId)}`,
  );
}

// ---------------------------------------------------------------------------
// Phase C3 — Direct messaging
// ---------------------------------------------------------------------------

export interface ConversationListItem {
  id: string;
  other_user: AuthorRef;
  last_message_at: string;
  unread_count: number;
  preview: string | null;
}

export interface MessageReadDto {
  id: string;
  conversation_id: string;
  sender_user_id: string;
  body: string;
  status: "active" | "deleted";
  created_at: string;
}

export interface ConversationMessagesResponse {
  messages: MessageReadDto[];
  has_more: boolean;
}

export function fetchConversations(): Promise<ConversationListItem[]> {
  return apiGet<{ conversations: ConversationListItem[]; total: number }>(
    "/api/community/conversations",
  ).then((r) => r.conversations);
}

export function startConversation(
  recipientUserId: string,
): Promise<ConversationListItem> {
  return apiPost<{ conversation: ConversationListItem }>(
    "/api/community/conversations",
    { recipient_user_id: recipientUserId },
  ).then((r) => r.conversation);
}

export function fetchMessages(
  conversationId: string,
  opts?: { since?: string; limit?: number },
): Promise<ConversationMessagesResponse> {
  const params = new URLSearchParams();
  if (opts?.since) params.set("since", opts.since);
  if (opts?.limit) params.set("limit", String(opts.limit));
  const qs = params.toString() ? `?${params.toString()}` : "";
  return apiGet<ConversationMessagesResponse>(
    `/api/community/conversations/${conversationId}/messages${qs}`,
  );
}

export function sendMessage(
  conversationId: string,
  body: string,
): Promise<MessageReadDto> {
  return apiPost<{ message: MessageReadDto }>(
    `/api/community/conversations/${conversationId}/messages`,
    { body },
  ).then((r) => r.message);
}

export function markConversationRead(conversationId: string): Promise<void> {
  return apiPost<void>(`/api/community/conversations/${conversationId}/read`);
}

export function deleteMessage(messageId: string): Promise<void> {
  return apiDelete<void>(`/api/community/messages/${messageId}`);
}

export function blockMember(
  userId: string,
  reason?: string,
): Promise<{ blocked_user_id: string; created_at: string }> {
  return apiPost(
    `/api/community/members/${userId}/block`,
    reason !== undefined ? { reason } : {},
  );
}

export function unblockMember(userId: string): Promise<void> {
  return apiDelete<void>(`/api/community/members/${userId}/block`);
}

export type DmReportReason =
  | "phi"
  | "spam"
  | "harassment"
  | "off_topic"
  | "other";

export function reportConversation(
  conversationId: string,
  reason: DmReportReason,
  note?: string,
): Promise<{ flag_id: string; status: "open" }> {
  return apiPost(
    `/api/community/conversations/${conversationId}/report`,
    note !== undefined ? { reason, note } : { reason },
  );
}

// ---------------------------------------------------------------------------
// Phase C4 — Notifications + moderation
// ---------------------------------------------------------------------------

export type NotificationType =
  | "reply"
  | "mention"
  | "reaction"
  | "moderation"
  | "announcement"
  | "dm";

export interface NotificationRead {
  id: string;
  type: NotificationType;
  actor: AuthorRef | null;
  target_type: string | null;
  target_id: string | null;
  thread_id: string | null;
  is_read: boolean;
  read_at: string | null;
  created_at: string;
}

export interface NotificationListResponse {
  notifications: NotificationRead[];
  unread_count: number;
  total: number;
  next_offset: number | null;
}

export function fetchNotifications(opts?: {
  unread?: boolean;
  limit?: number;
  offset?: number;
}): Promise<NotificationListResponse> {
  const params = new URLSearchParams();
  if (opts?.unread) params.set("unread", "true");
  if (opts?.limit !== undefined) params.set("limit", String(opts.limit));
  if (opts?.offset !== undefined) params.set("offset", String(opts.offset));
  const qs = params.toString() ? `?${params.toString()}` : "";
  return apiGet<NotificationListResponse>(`/api/community/notifications${qs}`);
}

export function markNotificationsRead(ids?: string[]): Promise<void> {
  return apiPost<void>(
    "/api/community/notifications/read",
    ids && ids.length > 0 ? { ids } : {},
  );
}

export interface EmailPreferences {
  community_email_opt_out: boolean;
  email_suppress_all: boolean;
}

export function fetchEmailPreferences(): Promise<EmailPreferences> {
  return apiGet<EmailPreferences>("/api/community/me/email-preferences");
}

export function updateEmailPreferences(body: {
  community_email_opt_out: boolean;
}): Promise<EmailPreferences> {
  return apiPatch<EmailPreferences>(
    "/api/community/me/email-preferences",
    body,
  );
}

export type FlagReason = "phi" | "spam" | "harassment" | "off_topic" | "other";
export type FlagTargetType = "thread" | "reply" | "profile";

export function createFlag(
  targetType: FlagTargetType,
  targetId: string,
  reason: FlagReason,
  note?: string,
): Promise<{ flag_id: string; status: "open" }> {
  return apiPost(
    `/api/community/${targetType}/${targetId}/flag`,
    note !== undefined ? { reason, note } : { reason },
  );
}

export interface FlagListItem {
  id: string;
  target_type: "thread" | "reply" | "profile" | "conversation";
  target_id: string;
  reporter: AuthorRef;
  reason: FlagReason;
  note: string | null;
  status: "open" | "reviewing" | "resolved" | "dismissed";
  created_at: string;
  target_preview: string | null;
}

export interface FlagListResponse {
  flags: FlagListItem[];
  total: number;
  next_offset: number | null;
}

export function fetchAdminFlagQueue(opts?: {
  status?: "open" | "reviewing" | "resolved" | "dismissed";
  limit?: number;
  offset?: number;
}): Promise<FlagListResponse> {
  const params = new URLSearchParams();
  if (opts?.status) params.set("status", opts.status);
  if (opts?.limit !== undefined) params.set("limit", String(opts.limit));
  if (opts?.offset !== undefined) params.set("offset", String(opts.offset));
  const qs = params.toString() ? `?${params.toString()}` : "";
  return apiGet<FlagListResponse>(`/api/community/admin/flags${qs}`);
}

export function resolveFlag(
  flagId: string,
  body: {
    resolution: "resolved" | "dismissed";
    note?: string;
    action?: "hide" | "lock" | "delete";
  },
): Promise<{
  flag_id: string;
  status: "resolved" | "dismissed";
  action_taken: "hide" | "lock" | "delete" | null;
}> {
  return apiPost(`/api/community/admin/flags/${flagId}/resolve`, body);
}
